import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import { useProfile } from '@contexts/ProfileContext';
import { usePermissions } from '@hooks/usePermissions';
import { getConfig } from '../config';
import {
  Megaphone,
  Menu,
  X,
  LogOut,
  User,
  ChevronDown,
  PanelLeftClose,
  PanelLeftOpen,
} from 'lucide-react';
import { clsx } from 'clsx';
import { NotificationBell } from './NotificationBell';
import { POSITION_LABELS } from '@/types/team';
import { MENU_ITEMS, PAGE_TITLES, type BaseMenuItem, type MenuItem } from '@/navigation/menu';
import { HelpButton } from './help/HelpButton';

/**
 * Layout principal da aplicação com sidebar e header (mesma identidade do ERPWebUI).
 */
export const DashboardLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem('sidebar-collapsed') === 'true';
    } catch {
      return false;
    }
  });
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const { profile, canManageTeam } = useProfile();
  const { checkPermission } = usePermissions();
  const location = useLocation();
  const navigate = useNavigate();

  // Environment banner
  const environment = getConfig()?.environment;
  const isNonProduction = environment && environment !== 'production';
  const envLabel = environment === 'development' ? 'Desenvolvimento' : environment === 'homologation' ? 'Homologação' : null;
  const envColor = environment === 'development' ? 'bg-blue-600' : environment === 'homologation' ? 'bg-amber-500' : '';

  useEffect(() => {
    try {
      localStorage.setItem('sidebar-collapsed', String(sidebarCollapsed));
    } catch {
      /* ignore */
    }
  }, [sidebarCollapsed]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Erro no logout:', error);
    }
  };

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const closeSidebar = () => setSidebarOpen(false);

  const menuItems = MENU_ITEMS;

  const isActivePath = (path?: string, exact = false) => {
    if (!path) return false;
    if (exact) return location.pathname === path || location.pathname === `${path}/`;
    return location.pathname.startsWith(path);
  };

  const hasMenuPermission = (item: BaseMenuItem) => {
    if (item.requiresTeamManager && !canManageTeam) return false;
    if (item.anyPermission && item.anyPermission.length > 0) {
      return item.anyPermission.some((p) => checkPermission(p).hasPermission);
    }
    if (!item.permission) return true;
    return checkPermission(item.permission).hasPermission;
  };

  const visibleMenuItems: MenuItem[] = menuItems
    .map((item) => {
      if (!hasMenuPermission(item)) return null;
      if (!('children' in item)) return item;
      const visibleChildren = item.children.filter((child) => hasMenuPermission(child));
      if (visibleChildren.length === 0) return null;
      return { ...item, children: visibleChildren };
    })
    .filter(Boolean) as MenuItem[];

  const pageTitle = PAGE_TITLES.find(([prefix]) => location.pathname.startsWith(prefix))?.[1] ?? '';

  const renderMenuItem = (item: MenuItem) => {
    if ('children' in item) {
      const key = item.key || item.label;
      const isOpen = !!openMenus[key];
      const isChildActive = item.children.some((c) => isActivePath(c.path, c.exact));

      if (sidebarCollapsed) {
        return (
          <div key={key} className="relative group">
            <button
              onClick={() => {
                setSidebarCollapsed(false);
                setOpenMenus({ [key]: true });
              }}
              className={clsx(
                'flex items-center justify-center w-full p-3 rounded-lg transition-colors',
                isChildActive
                  ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                  : 'text-slate-300 hover:text-white hover:bg-slate-700',
              )}
              title={item.label}
            >
              <item.icon size={20} />
            </button>
            <div className="absolute left-full top-0 ml-2 px-2 py-1 bg-slate-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none z-50 transition-opacity">
              {item.label}
            </div>
          </div>
        );
      }

      return (
        <div key={key}>
          <button
            onClick={() =>
              setOpenMenus((prev) => {
                const willOpen = !prev[key];
                if (willOpen) return { [key]: true };
                return { ...prev, [key]: false };
              })
            }
            className={clsx(
              'flex items-center justify-between w-full gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors',
              isChildActive
                ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                : 'text-slate-300 hover:text-white hover:bg-slate-700',
            )}
          >
            <div className="flex items-center gap-3">
              <item.icon size={20} />
              {item.label}
            </div>
            <ChevronDown size={16} className={clsx((isOpen || isChildActive) && 'transform rotate-180')} />
          </button>

          {(isOpen || isChildActive) && (
            <div className="mt-1 space-y-1">
              {item.children.map((child) => (
                <Link
                  key={child.path}
                  to={child.path}
                  className={clsx(
                    'flex items-center gap-3 pl-8 pr-4 py-2 text-sm font-medium rounded-lg transition-colors',
                    isActivePath(child.path, child.exact)
                      ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                      : 'text-slate-300 hover:text-white hover:bg-slate-700',
                  )}
                  onClick={closeSidebar}
                >
                  <child.icon size={18} />
                  {child.label}
                </Link>
              ))}
            </div>
          )}
        </div>
      );
    }

    const linkItem = item;

    if (sidebarCollapsed) {
      return (
        <div key={linkItem.path} className="relative group">
          <Link
            to={linkItem.path}
            className={clsx(
              'flex items-center justify-center p-3 rounded-lg transition-colors',
              isActivePath(linkItem.path)
                ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                : 'text-slate-300 hover:text-white hover:bg-slate-700',
            )}
            onClick={closeSidebar}
            title={linkItem.label}
          >
            <linkItem.icon size={20} />
          </Link>
          <div className="absolute left-full top-0 ml-2 px-2 py-1 bg-slate-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none z-50 transition-opacity">
            {linkItem.label}
          </div>
        </div>
      );
    }

    return (
      <Link
        key={linkItem.path}
        to={linkItem.path}
        className={clsx(
          'flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors',
          isActivePath(linkItem.path)
            ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
            : 'text-slate-300 hover:text-white hover:bg-slate-700',
        )}
        onClick={closeSidebar}
      >
        <linkItem.icon size={20} />
        {linkItem.label}
      </Link>
    );
  };

  const positionLabel = profile?.is_admin
    ? 'Administrador'
    : profile?.member
      ? POSITION_LABELS[profile.member.position]
      : user?.role;

  return (
    <div className="flex h-screen bg-slate-50">
      {/* Sidebar Mobile Backdrop */}
      {sidebarOpen && <div className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden" onClick={closeSidebar} />}

      {/* Sidebar */}
      <aside
        className={clsx(
          'fixed lg:static inset-y-0 left-0 bg-slate-900 text-white flex flex-col z-50 transform transition-all duration-200 ease-in-out',
          sidebarCollapsed ? 'w-16' : 'w-64',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        )}
      >
        {/* Logo */}
        <div className="p-4 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-lg flex items-center justify-center flex-shrink-0">
              <Megaphone size={20} />
            </div>
            {!sidebarCollapsed && (
              <div>
                <h1 className="text-xs text-slate-400 font-bold">Marketing Viver Organizer</h1>
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className={clsx('flex-1 space-y-2 overflow-y-auto', sidebarCollapsed ? 'p-2' : 'p-4')}>
          {visibleMenuItems.map(renderMenuItem)}
        </nav>

        {/* Collapse toggle (desktop only) */}
        <div className="hidden lg:block border-t border-slate-800">
          <button
            onClick={() => setSidebarCollapsed((prev) => !prev)}
            className="w-full p-3 text-slate-400 hover:text-white hover:bg-slate-800 transition-colors flex items-center justify-center"
            title={sidebarCollapsed ? 'Expandir menu' : 'Recolher menu'}
          >
            {sidebarCollapsed ? <PanelLeftOpen size={20} /> : <PanelLeftClose size={20} />}
          </button>
        </div>

        {/* User Info */}
        <div className="p-4 border-t border-slate-800">
          <div className={clsx('flex items-center text-sm', sidebarCollapsed ? 'justify-center' : 'gap-3')}>
            <div className="w-8 h-8 gradient-user-avatar rounded-full flex items-center justify-center flex-shrink-0">
              <User size={16} />
            </div>
            {!sidebarCollapsed && (
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{user?.name}</p>
                <p className="text-xs text-slate-400 truncate">{user?.email}</p>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Environment Banner */}
        {isNonProduction && envLabel && (
          <div className={clsx('text-white text-center text-xs font-semibold py-1 tracking-wide', envColor)}>
            ⚠ Ambiente de {envLabel}
          </div>
        )}

        {/* Header */}
        <header className="bg-white border-b border-slate-200 px-4 lg:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={toggleSidebar}
                className="lg:hidden p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
              >
                {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
              </button>

              <div className="hidden sm:block">
                <h2 className="text-xl font-semibold text-slate-900">{pageTitle}</h2>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Sino: atrasadas, vencendo hoje, críticas e delegadas — ver NotificationBell.tsx */}
              <NotificationBell />

              {/* Ajuda da tela atual (documentação em src/docs) */}
              <HelpButton />

              {/* User menu */}
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <div className="w-8 h-8 gradient-user-avatar rounded-full flex items-center justify-center">
                    <User size={16} className="text-white" />
                  </div>
                  <span className="hidden sm:inline text-sm font-medium">{user?.name}</span>
                  <ChevronDown size={16} />
                </button>

                {userMenuOpen && (
                  <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-50">
                    <div className="px-3 py-2 border-b border-slate-200">
                      <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                      <p className="text-xs text-slate-500">{user?.email}</p>
                      {positionLabel && <p className="text-xs text-slate-500">{positionLabel}</p>}
                      {!profile?.member && !profile?.is_admin && (
                        <p className="text-xs text-amber-600 mt-1">Sem cadastro na equipe de marketing</p>
                      )}
                    </div>

                    <div className="border-t border-slate-200 mt-1 pt-1">
                      <button
                        onClick={handleLogout}
                        className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                      >
                        <LogOut size={14} />
                        Sair
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto p-4 lg:p-6">
          <Outlet />
        </main>
      </div>

      {/* Click outside to close user menu */}
      {userMenuOpen && <div className="fixed inset-0 z-30" onClick={() => setUserMenuOpen(false)} />}
    </div>
  );
};
